/**
 * Sun shadow frustum fitting (2026-08-01 "everything casts shadows" pass).
 *
 * The scene has exactly ONE shadow-casting light: the warm key sun created in
 * render/world.ts and configured in render/riderCaster.ts. Until now its
 * orthographic frustum was a ±6 m box sized for the rider-shadow proxy alone,
 * so the rider was the only thing in the world that could cast — vehicles,
 * buildings and trees all floated shadowless.
 *
 * This module fits ONE ortho frustum around a CORRIDOR BOX expressed in the
 * camera-rig frame (the frame the sun is parented into): a slab of world that
 * covers the visible road ahead, the roadside strip the props stand on, and the
 * height band a caster can usefully occupy. Everything is static — the box and
 * the sun offset never change — so the fit runs once at construction.
 *
 * WHY A BOX AND NOT A SPHERE: a sphere fit is stable under rotation but wastes
 * ~40% of the map on this very elongated corridor. The sun direction is fixed
 * in the rig frame, so the tight box fit has no shimmer problem: the frustum
 * only ever translates with the rig, and translation of both caster and frustum
 * together is exactly what a shadow map wants.
 *
 * TEXEL BUDGET. The fitted frustum for the shipped box is roughly 55 × 70 m of
 * light-space area (see `SHADOW_BOX` below and the unit test), i.e. ~30 texels
 * per metre at a 2048² map and ~55 at 3584². That is a big step down from the
 * old rider-only box (170 texels/m) and it is the price of the feature: one map
 * cannot be both a 12 m hero-shadow lens and a 90 m world lens. The map size
 * was picked by measurement — see SHADOW_MAP_SIZE in render/riderCaster.ts.
 */

/** Axis-aligned box in the rig frame (metres). -Z is ahead of the rider. */
export interface ShadowBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

/** Orthographic frustum parameters for a DirectionalLightShadow camera. */
export interface OrthoFrustum {
  left: number;
  right: number;
  top: number;
  bottom: number;
  near: number;
  far: number;
}

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}
function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}
function norm(a: Vec3): Vec3 {
  const l = Math.hypot(a.x, a.y, a.z) || 1;
  return { x: a.x / l, y: a.y / l, z: a.z / l };
}

/**
 * Fit an orthographic shadow frustum around `box`, for a directional light that
 * sits at `lightPos` and aims at `target`.
 *
 * The basis reproduces three.js's own `Object3D.lookAt` for a light whose up is
 * +Y: view -Z points at the target, view +X is horizontal. The box corners are
 * projected into that basis and the extents are taken directly, with `margin`
 * added on every side so a caster straddling the boundary does not clip its own
 * shadow at the edge of the map.
 *
 * Pure arithmetic (no three.js) so it is unit-testable and runs before any
 * renderer exists.
 */
export function fitOrthoShadow(
  lightPos: Vec3,
  target: Vec3,
  box: ShadowBox,
  margin = 1.5,
): OrthoFrustum {
  const zAxis = norm(sub(lightPos, target)); // view +Z (points BACK at the light)
  let xAxis = cross({ x: 0, y: 1, z: 0 }, zAxis);
  if (Math.hypot(xAxis.x, xAxis.y, xAxis.z) < 1e-6) {
    // Degenerate: the light is straight overhead. Any horizontal axis works.
    xAxis = { x: 1, y: 0, z: 0 };
  }
  xAxis = norm(xAxis);
  const yAxis = cross(zAxis, xAxis);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minDepth = Infinity;
  let maxDepth = -Infinity;
  for (const cx of [box.minX, box.maxX]) {
    for (const cy of [box.minY, box.maxY]) {
      for (const cz of [box.minZ, box.maxZ]) {
        const v = sub({ x: cx, y: cy, z: cz }, lightPos);
        const px = dot(v, xAxis);
        const py = dot(v, yAxis);
        // The camera looks down its own -Z, so depth = -(view z).
        const pd = -dot(v, zAxis);
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
        if (pd < minDepth) minDepth = pd;
        if (pd > maxDepth) maxDepth = pd;
      }
    }
  }

  return {
    left: minX - margin,
    right: maxX + margin,
    bottom: minY - margin,
    top: maxY + margin,
    near: Math.max(0.1, minDepth - margin),
    far: maxDepth + margin,
  };
}

/** Texels per metre along each frustum axis at a given (square) map size. */
export function shadowTexelDensity(
  f: OrthoFrustum,
  mapSize: number,
): { x: number; y: number } {
  return {
    x: mapSize / (f.right - f.left),
    y: mapSize / (f.top - f.bottom),
  };
}
